/**
 * ProfileLinkInput (web): the pill input shared by the Social and Contact
 * sheets.
 *
 * Two jobs beyond a plain `<input>`:
 * - Renders the channel's fixed prefix ("linkedin.com/in/", "t.me/") inside the
 *   pill so the user only types the part that varies. Pasting a full URL still
 *   works - it is collapsed back to the handle on commit.
 * - Filters keystrokes by field kind, which is what stops a phone field from
 *   accepting letters instead of merely complaining about them afterwards.
 *
 * The value is held locally and committed on blur/Enter so typing never
 * re-renders the whole builder tree.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  filterPublicProfileLinkInput,
  isPublicProfileLinkInputValid,
  splitPublicProfileLink,
  toStoredPublicProfileLink,
  type PublicProfileLinkField,
} from '../../lib/profile-link-fields';

const FONT_STACK =
  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif";

export interface ProfileLinkInputProps {
  field: PublicProfileLinkField;
  /** Stored value (a full URL, a phone, an email - whatever the field stores). */
  value: string;
  placeholder: string;
  errorText: string;
  /** Called with the normalized value to store, or `''` to clear the channel. */
  onCommit: (stored: string) => void;
  /**
   * Company socials refuse to enable a row until a valid value is committed, so
   * they need to know a commit was rejected. Optional elsewhere.
   */
  onInvalid?: () => void;
}

export function ProfileLinkInput({
  field,
  value,
  placeholder,
  errorText,
  onCommit,
  onInvalid,
}: ProfileLinkInputProps) {
  // The prefix is derived from the stored value, not fixed per field: a stored
  // `linkedin.com/company/acme` shows `linkedin.com/company/`, not `/in/`.
  const external = splitPublicProfileLink(field, value);
  const [draft, setDraft] = useState(external.value);
  const [prefix, setPrefix] = useState(external.prefix);
  const [focused, setFocused] = useState(false);
  const [showError, setShowError] = useState(false);
  const externalRef = useRef(external.value);
  const draftRef = useRef(draft);
  const prefixRef = useRef(prefix);
  draftRef.current = draft;
  prefixRef.current = prefix;

  // Adopt changes that came from elsewhere (hydration, model reset) without
  // stomping on what the user is currently typing.
  useEffect(() => {
    if (external.value !== externalRef.current) {
      externalRef.current = external.value;
      setDraft(external.value);
      setPrefix(external.prefix);
      setShowError(false);
    }
  }, [external.value, external.prefix]);

  const commit = () => {
    const next = draftRef.current.trim();
    if (next === externalRef.current) return;
    if (!next) {
      setShowError(false);
      externalRef.current = '';
      if (value) onCommit('');
      return;
    }
    if (!isPublicProfileLinkInputValid(field, next, prefixRef.current)) {
      setShowError(true);
      onInvalid?.();
      return;
    }
    const stored = toStoredPublicProfileLink(field, next, prefixRef.current);
    setShowError(false);
    // Show the canonical form back to the user, so a pasted URL visibly
    // collapses to the handle instead of leaving them guessing.
    const canonical = splitPublicProfileLink(field, stored);
    externalRef.current = canonical.value;
    setDraft(canonical.value);
    setPrefix(canonical.prefix);
    onCommit(stored);
  };

  // Closing the dialog can unmount the field without ever blurring it, which
  // would silently drop the last thing typed.
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => () => commitRef.current(), []);

  /**
   * Phone numbers, handles and links are always LTR, even on an Arabic page.
   * `dir="auto"` is not enough: "+971..." starts with a NEUTRAL character, so
   * auto resolves the run to the RTL paragraph direction and the "+" jumps to
   * the end ("971500000001+"). Free text (Tabbio Message) keeps `auto`.
   */
  const dir = field.kind === 'text' ? 'auto' : 'ltr';

  const borderColor = focused
    ? 'var(--text-secondary)'
    : showError
      ? '#d93025'
      : 'var(--input-border)';

  return (
    <>
      <div
        dir={dir}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: 44,
          borderRadius: 9999,
          padding: '0 16px',
          gap: 1,
          background: 'var(--input-bg)',
          border: `1px solid ${borderColor}`,
          boxShadow: focused ? 'none' : 'var(--gem-input-shadow)',
          outline: 'none',
          transition: 'border-color 140ms, box-shadow 140ms',
          boxSizing: 'border-box',
        }}
      >
        {prefix ? (
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              fontSize: 14,
              fontFamily: FONT_STACK,
              color: 'var(--text-tertiary)',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {prefix}
          </span>
        ) : null}
        <input
          type={inputTypeFor(field)}
          inputMode={inputModeFor(field)}
          dir={dir}
          value={draft}
          onChange={(event) => {
            setDraft(filterPublicProfileLinkInput(field, event.target.value));
            setShowError(false);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            commit();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
          placeholder={placeholder}
          spellCheck={false}
          autoCapitalize="none"
          autoCorrect="off"
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            border: 'none',
            background: 'transparent',
            padding: 0,
            fontSize: 14,
            fontFamily: FONT_STACK,
            color: 'var(--text-primary)',
            outline: 'none',
          }}
        />
      </div>
      {showError ? (
        <div
          style={{
            fontSize: 12,
            lineHeight: '16px',
            color: '#d93025',
            padding: '0 4px',
            fontFamily: FONT_STACK,
          }}
        >
          {errorText}
        </div>
      ) : null}
    </>
  );
}

function inputTypeFor(field: PublicProfileLinkField) {
  if (field.kind === 'email') return 'email';
  if (field.kind === 'phone') return 'tel';
  if (field.kind === 'url') return 'url';
  return 'text';
}

function inputModeFor(field: PublicProfileLinkField) {
  if (field.kind === 'email') return 'email' as const;
  if (field.kind === 'phone') return 'tel' as const;
  if (field.kind === 'url') return 'url' as const;
  return 'text' as const;
}
